import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
    MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CorporateTaxService } from '../../../services/corporateTax.service';
import { LedgerCloseSettingsService } from '../../../services/ledger-close-settings.service';
import {
    AdjustmentInput, ComputationRequest, CorporateTaxSettings, TAXPAYER_CATEGORIES, TaxComputation, TaxpayerCategory,
} from '../../../interfaces/corporateTax.interface';
import { useToast } from '../../../context/ToastContext';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { formatDate } from '../../../utils/dateUtils';
import AttachmentsPanel from '../../common/AttachmentsPanel';
import ConfirmDialog from '../../common/ConfirmDialog';
import {
    DOC_STATUS_COLOR, TAXPAYER_LABEL, currentIncomeYear, defaultComputationYear, fiscalYearStartIso, incomeYearLabel, incomeYearOptions,
} from './corporateTaxText';

interface Props {
    institutionId: string; startMonth: number; settings: CorporateTaxSettings | null; canManage: boolean; canPost: boolean;
}

const Row: React.FC<{ label: string; value: React.ReactNode; bold?: boolean }> = ({ label, value, bold }) => (
    <TableRow>
        <TableCell sx={{ fontWeight: bold ? 600 : undefined }}>{label}</TableCell>
        <TableCell align="right" sx={{ fontWeight: bold ? 600 : undefined }}>{value}</TableCell>
    </TableRow>
);

/** Current-tax computation: profit, add-backs, adjustments, regular vs minimum vs final-source tax. */
const ComputationTab: React.FC<Props> = ({ institutionId, startMonth, settings, canManage, canPost }) => {
    const toast = useToast();
    const [rows, setRows] = useState<TaxComputation[]>([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | undefined>();
    const [incomeYear, setIncomeYear] = useState(currentIncomeYear(startMonth));
    const [category, setCategory] = useState<TaxpayerCategory | ''>('');
    const [adjustments, setAdjustments] = useState<AdjustmentInput[]>([]);
    const [includeDep, setIncludeDep] = useState(false);
    const [notes, setNotes] = useState('');
    const [result, setResult] = useState<TaxComputation | null>(null);
    const [busy, setBusy] = useState(false);
    const [closedThrough, setClosedThrough] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<{ kind: 'post' | 'reverse' | 'delete'; row: TaxComputation } | null>(null);

    const load = useCallback(async () => {
        try {
            setRows(await CorporateTaxService.listComputations(institutionId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load tax computations');
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        (async () => {
            try { setClosedThrough((await LedgerCloseSettingsService.get()).closedThrough ?? null); }
            catch { setClosedThrough(null); }
        })();
    }, []);

    const request = (): ComputationRequest => ({
        fiscalYearStart: fiscalYearStartIso(incomeYear, startMonth),
        taxpayerCategory: category || null,
        adjustments: adjustments.filter(a => a.label.trim() !== ''),
        includeDepreciationAdjustment: includeDep,
        notes: notes || null,
    });

    const openNew = async () => {
        setEditingId(undefined); setIncomeYear(currentIncomeYear(startMonth) - 1); setCategory('');
        setAdjustments([]); setIncludeDep(false); setNotes(''); setResult(null); setOpen(true);
        try {
            const rates = await CorporateTaxService.listRates(institutionId);
            setIncomeYear(defaultComputationYear(rates.map(r => r.incomeYear), startMonth));
        } catch {
            // no rates to look at: keep the previous-year default
        }
    };

    const openExisting = (r: TaxComputation) => {
        setEditingId(r.id ?? undefined);
        setIncomeYear(Number(r.fiscalYearStart.substring(0, 4)));
        setCategory(r.taxpayerCategory);
        setAdjustments(r.adjustments.filter(a => a.source === 'MANUAL').map(a => ({ label: a.label, nature: a.nature, amount: a.amount })));
        setIncludeDep(r.adjustments.some(a => a.source === 'DEPRECIATION'));
        setNotes(r.notes ?? '');
        setResult(r);
        setOpen(true);
    };

    const preview = async () => {
        setBusy(true);
        try { setResult(await CorporateTaxService.previewComputation(institutionId, request())); }
        catch (e) { toast.showApiError(e, 'Failed to compute the tax'); }
        finally { setBusy(false); }
    };

    const save = async () => {
        setBusy(true);
        try {
            const saved = await CorporateTaxService.saveComputation(institutionId, request(), editingId);
            toast.showSuccess('Tax computation saved as a draft');
            setEditingId(saved.id ?? undefined);
            setResult(saved);
            await load();
        } catch (e) { toast.showApiError(e, 'Failed to save the computation'); }
        finally { setBusy(false); }
    };

    const runConfirmed = async () => {
        if (!confirm || !confirm.row.id) return;
        setBusy(true);
        try {
            if (confirm.kind === 'post') await CorporateTaxService.postComputation(institutionId, confirm.row.id);
            if (confirm.kind === 'reverse') await CorporateTaxService.reverseComputation(institutionId, confirm.row.id);
            if (confirm.kind === 'delete') await CorporateTaxService.deleteComputation(institutionId, confirm.row.id);
            toast.showSuccess(confirm.kind === 'post' ? 'Tax provision posted' : confirm.kind === 'reverse' ? 'Provision reversed' : 'Draft deleted');
            setConfirm(null);
            setOpen(false);
            await load();
        } catch (e) { toast.showApiError(e, 'The action failed'); }
        finally { setBusy(false); }
    };

    const setAdj = (i: number, patch: Partial<AdjustmentInput>) =>
        setAdjustments(prev => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));

    const isDraft = result?.status === 'DRAFT';
    const editable = !result || !result.id || isDraft;
    const closedWarning = closedThrough && result && result.fiscalYearEnd <= closedThrough;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                {canManage && <Button data-help="accounting.corpTax.comp.new" variant="contained" onClick={openNew}>New computation</Button>}
            </Box>
            <TableContainer component={Paper}>
                <Table data-help-section="accounting.corpTax.comp.table" size="small" aria-label="Tax computations">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.corpTax.comp.col.year">Fiscal year</TableCell><TableCell data-help="accounting.corpTax.comp.col.income" align="right">Taxable income</TableCell>
                            <TableCell data-help="accounting.corpTax.comp.col.charge" align="right">Tax charge</TableCell><TableCell data-help="accounting.corpTax.comp.col.basis">Basis</TableCell><TableCell data-help="accounting.corpTax.comp.col.status">Status</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => (
                            <TableRow key={r.id ?? r.fiscalYearStart} hover>
                                <TableCell>{formatDate(r.fiscalYearStart)} to {formatDate(r.fiscalYearEnd)}</TableCell>
                                <TableCell align="right">{formatMoney(r.taxableIncome)}</TableCell>
                                <TableCell align="right">{formatMoney(r.taxCharge)}</TableCell>
                                <TableCell>{humanizeEnum(r.chargeBasis)}</TableCell>
                                <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={DOC_STATUS_COLOR[r.status]} /></TableCell>
                                <TableCell><Button data-help="accounting.corpTax.comp.action.open" size="small" onClick={() => openExisting(r)}>Open</Button></TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && <TableRow><TableCell colSpan={6}>No computations yet.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog data-help-page="accounting.corpTax.comp.form.page" open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Corporate tax computation</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        <TextField data-help="accounting.corpTax.comp.form.year" select size="small" label="Income year" value={incomeYear} disabled={!!editingId || !editable}
                            sx={{ flex: '1 1 180px', maxWidth: 240 }} onChange={e => setIncomeYear(Number(e.target.value))}>
                            {incomeYearOptions(startMonth).map(y => <MenuItem key={y} value={y}>{incomeYearLabel(y, startMonth)}</MenuItem>)}
                        </TextField>
                        <TextField data-help="accounting.corpTax.comp.form.category" select size="small" label="Taxpayer category" value={category} disabled={!editable}
                            sx={{ flex: '1 1 220px', maxWidth: 300 }} onChange={e => setCategory(e.target.value as TaxpayerCategory | '')}
                            helperText={settings?.taxpayerCategory ? `Settings default: ${TAXPAYER_LABEL[settings.taxpayerCategory]}` : 'Set a default in Settings'}>
                            <MenuItem value="">Use settings default</MenuItem>
                            {TAXPAYER_CATEGORIES.map(c => <MenuItem key={c} value={c}>{TAXPAYER_LABEL[c]}</MenuItem>)}
                        </TextField>
                    </Box>

                    <Typography variant="subtitle2">Adjustments to profit</Typography>
                    {adjustments.map((a, i) => (
                        <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <TextField data-help="accounting.corpTax.comp.form.adjDesc" size="small" label="Description" value={a.label} disabled={!editable}
                                sx={{ flex: '2 1 220px' }} onChange={e => setAdj(i, { label: e.target.value })} />
                            <TextField data-help="accounting.corpTax.comp.form.adjNature" select size="small" label="Nature" value={a.nature} disabled={!editable}
                                sx={{ flex: '1 1 140px', maxWidth: 180 }} onChange={e => setAdj(i, { nature: e.target.value as AdjustmentInput['nature'] })}>
                                <MenuItem value="PERMANENT">Permanent</MenuItem>
                                <MenuItem value="TEMPORARY">Temporary</MenuItem>
                            </TextField>
                            <TextField data-help="accounting.corpTax.comp.form.adjAmount" size="small" label="Amount (+ adds, - deducts)" type="number" value={a.amount} disabled={!editable}
                                sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => setAdj(i, { amount: Number(e.target.value) })} />
                            {editable && (
                                <IconButton data-help="accounting.corpTax.comp.removeAdjustment" aria-label="Remove adjustment" onClick={() => setAdjustments(prev => prev.filter((_, idx) => idx !== i))}>
                                    <DeleteOutlineIcon />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                    {editable && (
                        <Box>
                            <Button data-help="accounting.corpTax.comp.addAdjustment" size="small" onClick={() => setAdjustments(prev => [...prev, { label: '', nature: 'PERMANENT', amount: 0 }])}>
                                Add adjustment
                            </Button>
                        </Box>
                    )}
                    <FormControlLabel data-help="accounting.corpTax.comp.form.includeDep" disabled={!editable}
                        control={<Checkbox checked={includeDep} onChange={e => setIncludeDep(e.target.checked)} />}
                        label="Add the book vs tax depreciation difference (taken from this year's posted deferred tax working; do not enter tax depreciation above)" />
                    <TextField data-help="accounting.corpTax.comp.form.notes" size="small" label="Notes" value={notes} multiline minRows={2} fullWidth disabled={!editable}
                        onChange={e => setNotes(e.target.value)} />

                    {result && (
                        <>
                            {result.warnings.map(w => <Alert key={w} severity="warning">{w}</Alert>)}
                            {closedWarning && (
                                <Alert severity="warning">
                                    The ledger is closed through {formatDate(closedThrough!)}; posting inside a closed period will be refused.
                                </Alert>
                            )}
                            <TableContainer component={Paper} variant="outlined">
                                <Table data-help-section="accounting.corpTax.comp.breakdown" size="small" aria-label="Computation breakdown">
                                    <TableBody>
                                        <Row label="Recorded net profit" value={formatMoney(result.recordedNetProfit)} />
                                        <Row label="Add back: income tax expense" value={formatMoney(result.taxExpenseAddBack)} />
                                        <Row label="Profit before tax" value={formatMoney(result.profitBeforeTax)} />
                                        {result.adjustments.map((a, i) => (
                                            <Row key={i} label={`${a.label} (${humanizeEnum(a.nature)})`} value={formatMoney(a.amount)} />
                                        ))}
                                        <Row label="Taxable income" value={formatMoney(result.taxableIncome)} bold />
                                        <Row label={`Regular tax at ${result.regularRatePct}%`} value={formatMoney(result.regularTax)} />
                                        <Row label={`Minimum tax at ${result.minimumTaxRatePct}% of gross receipts ${formatMoney(result.grossReceipts)}`}
                                            value={formatMoney(result.minimumTax)} />
                                        {result.exportSourceTaxFinal && (
                                            <Row label="Final source tax (export, treated as final liability)" value={formatMoney(result.finalSourceTax)} />
                                        )}
                                        <Row label={`Tax charge (${humanizeEnum(result.chargeBasis)})`} value={formatMoney(result.taxCharge)} bold />
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {result.id && (
                                <AttachmentsPanel entityType="CORPORATE_TAX_COMPUTATION" entityId={result.id} canManage={canManage} />
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.corpTax.comp.form.close" onClick={() => setOpen(false)}>Close</Button>
                    {editable && canManage && <Button data-help="accounting.corpTax.comp.preview" disabled={busy} onClick={preview}>Preview</Button>}
                    {editable && canManage && <Button data-help="accounting.corpTax.comp.saveDraft" variant="outlined" disabled={busy} onClick={save}>Save draft</Button>}
                    {isDraft && result?.id && canManage && (
                        <Button data-help="accounting.corpTax.comp.deleteDraft" color="error" disabled={busy} onClick={() => setConfirm({ kind: 'delete', row: result })}>Delete draft</Button>
                    )}
                    {isDraft && result?.id && canPost && (
                        <Button data-help="accounting.corpTax.comp.post" variant="contained" disabled={busy} onClick={() => setConfirm({ kind: 'post', row: result })}>Post provision</Button>
                    )}
                    {result?.status === 'POSTED' && canPost && (
                        <Button data-help="accounting.corpTax.comp.reverse" color="error" disabled={busy} onClick={() => setConfirm({ kind: 'reverse', row: result })}>Reverse</Button>
                    )}
                </DialogActions>
            </Dialog>

            <ConfirmDialog open={confirm !== null} submitting={busy}
                title={confirm?.kind === 'post' ? 'Post the tax provision?' : confirm?.kind === 'reverse' ? 'Reverse the provision?' : 'Delete this draft?'}
                message={confirm?.kind === 'post'
                    ? 'Debits current tax expense and credits income tax payable for the tax charge.'
                    : confirm?.kind === 'reverse' ? 'Posts an exact reversing journal. A computation with a set-off must have the set-off reversed first.'
                        : 'The draft computation is removed.'}
                confirmLabel={confirm?.kind === 'post' ? 'Post' : confirm?.kind === 'reverse' ? 'Reverse' : 'Delete'}
                confirmColor={confirm?.kind === 'post' ? 'primary' : 'error'}
                onCancel={() => setConfirm(null)} onConfirm={runConfirmed} />
        </Box>
    );
};

export default ComputationTab;
